import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
} from '@mui/material';
import { GenericModal } from '@/components/shared/GenericModal';
import { dialogOverlay } from '../../Insights/NewComponent/styles';
import { EditCellModalData } from '@/types';
type Props = {
  open: boolean;
  onClose: () => void;
  saveBulkEdit?: (newValue: unknown) => Promise<void> | void;
  cellData?: EditCellModalData | null;
  saveSingleEdit?: (itemId: string, columnKey: string, value: unknown) => Promise<void>;
  onSuccess?: () => void;
  onError?: (err?: unknown) => void;
  fetchFilteredAssemblies?: () => void;
};

export const EditCellModal = ({
  open,
  onClose,
  saveBulkEdit,
  cellData,
  saveSingleEdit,
  onSuccess,
  onError,
  fetchFilteredAssemblies,
}: Props) => {
  const [editValue, setEditValue] = useState<unknown>('');
  const [saving, setSaving] = useState(false);
  const { currentValue, columnKey, itemId, columnName, name } = cellData ?? {};
  const cellType = cellData?.cellType;
  const options = cellData?.options;
  useEffect(() => {
    if (open) {
      if (cellType === 'DATE') {
        try {
          const date = currentValue ? new Date(currentValue) : null;
          setEditValue(date ? date.toISOString().slice(0, 10) : '');
        } catch (_err) {
          setEditValue(currentValue ?? '');
        }
      } else {
        setEditValue(currentValue ?? '');
      }
      setSaving(false);
    }
  }, [open, currentValue, cellType]);

  const handleSave = async () => {
    setSaving(true);
    try {
      if (saveBulkEdit) {
        await saveBulkEdit(editValue);
        onSuccess?.();
        onClose();
        return;
      }
      if (saveSingleEdit && itemId && columnKey) {
        await saveSingleEdit(itemId, columnKey, editValue);
        onSuccess?.();
        fetchFilteredAssemblies?.();
      }
      onClose();
    } catch (error) {
      console.error('Failed to save cell edit:', error);
      onError?.(error);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditValue(currentValue ?? '');
    onClose();
  };

  return (
    <GenericModal
      sx={{ ...dialogOverlay, height: 'fit-content' }}
      open={open}
      handleClose={handleCancel}
      title={`Edit ${columnName}`}
    >
      <Box sx={{ p: 3, minWidth: '400px' }}>
        {name && (
          <Typography variant="body2" sx={{ mb: 2 }}>
            Component: {name}
          </Typography>
        )}
        {cellType === 'DATE' ? (
          <FormControl fullWidth sx={{ mb: 3 }} variant="outlined">
            <InputLabel shrink htmlFor="edit-cell-date">
              {`${columnName ?? ''} Value`}
            </InputLabel>

            <OutlinedInput
              id="edit-cell-date"
              type="date"
              value={editValue || ''}
              label={`${columnName ?? ''} Value`}
              onChange={(e) => setEditValue(e.target.value)}
              autoFocus
              onMouseDown={(e) => e.stopPropagation()}
              inputProps={{
                'aria-label': `${columnName ?? ''} Value`,
              }}
            />
          </FormControl>
        ) : cellType === 'DROPDOWN' ? (
          <FormControl fullWidth sx={{ mb: 3 }} variant="outlined">
            <InputLabel id="edit-cell-dropdown-label" shrink>
              {`${columnName ?? ''} Value`}
            </InputLabel>

            <Select
              labelId="edit-cell-dropdown-label"
              value={editValue ?? ''}
              label={`${columnName ?? ''} Value`}
              displayEmpty
              onChange={(e) => setEditValue(e.target.value)}
              onMouseDown={(e) => e.stopPropagation()}
              autoFocus
              MenuProps={{
                PaperProps: {
                  sx: {
                    '& .Mui-selected': {
                      backgroundColor: '#7E9BBC33 !important',
                    },
                  },
                },
              }}
            >
              <MenuItem value="">N/A</MenuItem>

              {options?.map((opt) => (
                <MenuItem key={opt} value={opt}>
                  {opt}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : (
          <TextField
            fullWidth
            type={cellType}
            label={`${columnName ?? ''} Value`}
            value={editValue}
            onChange={(e) =>
              setEditValue(cellType === 'NUMBER' ? Number(e.target.value) : String(e.target.value))
            }
            variant="outlined"
            sx={{ mb: 3, userSelect: 'text' }}
            autoFocus
            autoComplete="off"
            onMouseDown={(e) => e.stopPropagation()}
          />
        )}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button variant="outlined" onClick={handleCancel} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </Box>
      </Box>
    </GenericModal>
  );
};
